import { useMemo, useState } from 'react'
import { useCurrentUser } from '../hooks/useCurrentUser.ts'
import { CategoriesTable } from '../components/CategoriesTable.tsx'
import { useCategories, useCreateCategory, useDeleteCategory, useUpdateCategory } from '../hooks/useCategories.ts'
import { useCategoriesTree } from '../hooks/useCategoriesTree.ts'
import { useQuotes } from '../hooks/useQuotes.ts'
import { useCreateTicker, useDeleteTicker, useTickers, useUpdateTicker } from '../hooks/useTickers.ts'
import { TickerModal } from '../components/TickerModal.tsx'
import { CategoryModal } from '../components/CategoryModal.tsx'
import type { Category, Ticker } from '../types.ts'
import styles from './Categories.module.css'

type ModalState =
  | { open: false }
  | { open: true; ticker?: Ticker }

type CategoryModalState =
  | { open: false }
  | { open: true; category?: Category }

export function CategoriesPage() {
  const { userId } = useCurrentUser()

  // Categories
  const {
    data: categories,
    isLoading: isLoadingCategories,
    isError: isErrorCategories,
    error: categoriesError,
  } = useCategories(userId)

  // Tickers
  const {
    data: tickers,
    isLoading: isLoadingTickers,
    isError: isErrorTickers,
    error: tickersError,
  } = useTickers(userId)

  const tickerSymbols = useMemo(() => (tickers ?? []).map((item) => item.ticker), [tickers])

  // Quotes
  const {
    data: quotesData,
    isFetching: isFetchingQuotes,
    isError: isErrorQuotes,
    error: quotesError,
  } = useQuotes(tickerSymbols)
  const categoriesTree = useCategoriesTree({
    categories: categories ?? [],
    tickers: tickers ?? [],
    quotesBySymbol: quotesData?.bySymbol ?? {},
  })

  const createMutation = useCreateTicker(userId)
  const updateMutation = useUpdateTicker(userId)
  const deleteMutation = useDeleteTicker(userId)

  const createCategoryMutation = useCreateCategory(userId)
  const updateCategoryMutation = useUpdateCategory(userId)
  const deleteCategoryMutation = useDeleteCategory(userId)

  const [modal, setModal] = useState<ModalState>({ open: false })
  const [categoryModal, setCategoryModal] = useState<CategoryModalState>({ open: false })
  
  const [deletingId, setDeletingId] = useState<number | null>(null)
  const [deletingCategoryId, setDeletingCategoryId] = useState<number | null>(null)

  const isLoading = isLoadingCategories || isLoadingTickers
  const isSaving = createMutation.isPending || updateMutation.isPending
  
  const isSavingCategory = createCategoryMutation.isPending || updateCategoryMutation.isPending
  
  const error =
    categoriesError instanceof Error
      ? categoriesError.message
      : tickersError instanceof Error
        ? tickersError.message
        : 'Erro desconhecido'
  
  const hasNoCategories = (categories?.length ?? 0) === 0

  function openEdit(ticker: Ticker) {
    setModal({ open: true, ticker })
  }

  function openCreateTicker() {
    setModal({ open: true })
  }

  function closeModal() {
    setModal({ open: false })
  }

  function openCreateCategory() {
    setCategoryModal({ open: true })
  }

  function closeCategoryModal() {
    setCategoryModal({ open: false })
  }

  function openEditCategory(category: Category) {
    setCategoryModal({ open: true, category })
  }

  function handleSave(formData: {
    ticker: string
    cnpj: string | null
    qtd: string | null
    preco_medio: string | null
    category_id: number | null
  }) {
    if (!modal.open) return

    if (!modal.ticker) {
      createMutation.mutate(
        { ...formData, user_id: userId },
        { onSuccess: closeModal },
      )
      return
    }

    updateMutation.mutate(
      { id: modal.ticker.id, data: formData },
      { onSuccess: closeModal },
    )
  }

  function handleDelete(ticker: Ticker) {
    const confirmed = window.confirm(`Confirma excluir o ticker "${ticker.ticker}"?`)
    if (!confirmed) return

    setDeletingId(ticker.id)
    deleteMutation.mutate(ticker.id, {
      onSettled: () => setDeletingId(null),
    })
  }

  function handleSaveCategory(formData: {
    name: string
    slug: string
    parent_id: number | null
  }) {
    if (categoryModal.open && categoryModal.category) {
      updateCategoryMutation.mutate(
        { id: categoryModal.category.id, data: formData },
        { onSuccess: closeCategoryModal },
      )
      return
    }

    createCategoryMutation.mutate(formData, { onSuccess: closeCategoryModal })
  }

  function handleDeleteCategory(category: Category) {
    const confirmed = window.confirm(`Confirma excluir a categoria "${category.name}"?`)
    if (!confirmed) return

    setDeletingCategoryId(category.id)
    deleteCategoryMutation.mutate(category.id, {
      onSettled: () => setDeletingCategoryId(null),
      onError: (error) => {
        window.alert(error instanceof Error ? error.message : 'Erro ao deletar categoria')
      },
    })
  }

  return (
    <div className={styles.page}>
      <header className="header">
        <div className="header-title-row">
          <h1>Categorias</h1>
          <div className="header-actions">
            <button type="button" className="btn btn--primary btn--sm" onClick={openCreateCategory}>
              + Nova Categoria
            </button>
            <button type="button" className="btn btn--primary btn--sm" onClick={openCreateTicker}>
              + Novo Ticker
            </button>
          </div>
        </div>
        {/*<p className="subtitle">Árvore de categorias · user #{userId}</p>*/}
      </header>

      <main className="main">

        {/*<section className={`card ${styles.card}`}>*/}
          {/*
          <div className="card-header">
            <h2>Estrutura</h2>
          </div>
          */}
          {isLoading ? <div className="status">Carregando categorias...</div> : null}

          {!isLoading && (isErrorCategories || isErrorTickers) ? (
            <div className="status status--error">Erro: {error}</div>
          ) : null}

          {!isLoading && !isErrorCategories && !isErrorTickers && hasNoCategories ? (
            <div className="status">Nenhuma categoria cadastrada.</div>
          ) : null}

          {!isLoading && !isErrorCategories && !isErrorTickers && isFetchingQuotes ? (
            <div className="status">Atualizando cotações...</div>
          ) : null}

          {!isLoading && !isErrorCategories && !isErrorTickers && isErrorQuotes ? (
            <div className="status status--error">
              Erro ao buscar cotações:{' '}
              {quotesError instanceof Error ? quotesError.message : 'Erro desconhecido'}
            </div>
          ) : null}

          {!isLoading && !isErrorCategories && !isErrorTickers && !hasNoCategories ? (
            <CategoriesTable
              tree={categoriesTree.tree}
              tickersByCategory={categoriesTree.tickersByCategory}
              quotesBySymbol={quotesData?.bySymbol ?? {}}
              deletingId={deletingId}
              deletingCategoryId={deletingCategoryId}
              onEditTicker={openEdit}
              onDeleteTicker={handleDelete}
              onEditCategory={openEditCategory}
              onDeleteCategory={handleDeleteCategory}
            />
          ) : null}
        {/*</section>*/}

      </main>

      {modal.open && (
        <TickerModal
          ticker={modal.ticker}
          categories={categories ?? []}
          userId={userId}
          onClose={closeModal}
          onSave={handleSave}
          isSaving={isSaving}
        />
      )}

      {categoryModal.open && (
        <CategoryModal
          category={categoryModal.category}
          categories={categories ?? []}
          onClose={closeCategoryModal}
          onSave={handleSaveCategory}
          isSaving={isSavingCategory}
        />
      )}
    </div>
  )
}
